import type { UseTuningSession } from '../hooks/useTuningSession';
import { Card } from '@/components/ui/Card';
import { GoalCard, TipCard, InfoCard, WarningCard } from '../components/Cards';

interface BowGripData {
  /** Self-reported grip style — primary picker. */
  wristStyle?: 'neutral' | 'high' | 'low' | 'unsure';
  /** Where pressure lands on the palm — lifeline check. */
  pressurePoint?: 'thumb-pad-lifeline' | 'across-lifeline' | 'meaty-palm' | 'unsure';
  /** Are the front fingers RELAXED at full draw (could you flutter them)? */
  fingersRelaxed?: boolean;
  /** Bubble fight — constantly correcting the level at full draw? */
  bubbleFight?: boolean;
  /** Wrist sling installed? */
  wristSling?: boolean;
}

interface Props {
  tuning: UseTuningSession;
}

/**
 * Step 2 — Bow Grip Assessment
 *
 * Foundational. A torquey or inconsistent grip masks every downstream tuning
 * problem — chases that look like rest, spine, or cam-lean issues are really
 * grip pressure issues. Per John Dudley (Nock On / School of Nock):
 *
 *   "Your bow should always stay on the life line's thumb side."
 *   "You're loading the pad of your thumb, not your palm."
 *   "Those front fingers can hang completely loose. They're not gripping."
 *
 * We use Dudley's "lifeline" framing because it's the most actionable
 * physical landmark + cross-references the dominant compound coaching
 * authority.
 *
 * Sources: Nock On (nockonarchery.com/bow-grip, /get-a-grip-on-archery-accuracy,
 * /how-to-properly-grip-a-bow); Elite Archery (Woods, Sidebottom);
 * Lancaster Archery Supply; Bow International.
 */
export function BowGripStep({ tuning }: Props) {
  const data = (tuning.session.stepData['bow_grip'] as BowGripData | undefined) ?? {};
  const update = (patch: Partial<BowGripData>) =>
    tuning.updateStepData<BowGripData>('bow_grip', { ...data, ...patch });

  // Which grip is the user reporting? Drives the contextual coaching card.
  const isHighWrist = data.wristStyle === 'high';
  const isLowWrist = data.wristStyle === 'low';
  const isNeutral = data.wristStyle === 'neutral';

  const pressureWrong =
    data.pressurePoint === 'across-lifeline' || data.pressurePoint === 'meaty-palm';

  return (
    <>
      <GoalCard title="Why grip matters BEFORE tuning">
        Grip is the single biggest source of induced bow torque — and torque corrupts
        every downstream tuning metric. A bow that won't paper tune, a peep that won't
        stay rotated, persistent left/right misses, bubble fighting at full draw —
        these are often grip problems wearing tuning-problem clothes.
        <br /><br />
        <em className="text-zinc-300">
          The principle: your bow should always stay on the thumb side of your lifeline.
        </em>
      </GoalCard>

      {/* ───────────── Diagram: three grip styles, three views ───────────── */}
      <Card padding="lg">
        <h3 className="font-semibold mb-1">The three grips — visual reference</h3>
        <p className="text-[12px] text-zinc-400 mb-3 leading-relaxed">
          NEUTRAL (the recommended grip) vs HIGH WRIST vs LOW WRIST. Look at side
          view (wrist angle), palm view (pressure point), and front view (thumb
          direction down the arrow toward target).
        </p>
        <GripDiagram />
      </Card>

      {/* ───────────── Self-assessment: which wrist style are you using? ───────────── */}
      <Card padding="lg">
        <h3 className="font-semibold mb-1">Which grip style are you using?</h3>
        <p className="text-[12px] text-zinc-400 mb-3 leading-relaxed">
          Look at your hand at full draw — or take a phone photo at full draw. Pick
          the closest match to your wrist angle.
        </p>
        <div className="space-y-2">
          <Choice
            label="Neutral / relaxed — knuckles at ~45°, wrist relaxed back, pressure on thumb-pad lifeline"
            sub="The recommended grip. Standard for modern compounds."
            selected={isNeutral}
            tone="good"
            onClick={() => update({ wristStyle: 'neutral' })}
          />
          <Choice
            label="High wrist — wrist cocked UP, meaty thumb pad on throat of grip"
            sub="Common error on hunting compounds. Shortens effective DL + creates instability."
            selected={isHighWrist}
            tone="bad"
            onClick={() => update({ wristStyle: 'high' })}
          />
          <Choice
            label="Low wrist — wrist FLAT, palm pressed flat against the back of the grip"
            sub="Works on bows designed for it; risky if grip doesn't fit your hand."
            selected={isLowWrist}
            tone="warn"
            onClick={() => update({ wristStyle: 'low' })}
          />
          <Choice
            label="Not sure — I haven't looked / never been taught"
            selected={data.wristStyle === 'unsure'}
            tone="neutral"
            onClick={() => update({ wristStyle: 'unsure' })}
          />
        </div>
      </Card>

      {/* ───────────── Pressure point — the "lifeline" check (Dudley) ───────────── */}
      <Card padding="lg">
        <h3 className="font-semibold mb-1">Lifeline check — where does pressure land?</h3>
        <p className="text-[12px] text-zinc-400 mb-3 leading-relaxed">
          Hold your bow hand out palm-up. Bend it slightly — you'll see a main crease
          running from below your index finger around your thumb pad. <strong>That's
          your lifeline.</strong> The bow's grip pressure should land on the
          <strong> thumb-side of the lifeline</strong>, not across it or into the
          meaty palm.
        </p>
        <div className="space-y-2">
          <Choice
            label="On the thumb pad, thumb-side of the lifeline crease"
            sub="✓ Correct — loading the pad of your thumb, not your palm."
            selected={data.pressurePoint === 'thumb-pad-lifeline'}
            tone="good"
            onClick={() => update({ pressurePoint: 'thumb-pad-lifeline' })}
          />
          <Choice
            label="Across the lifeline crease — bow is too deep in my hand"
            sub="✗ Creates a long torque arm — any wrist tension twists the riser."
            selected={data.pressurePoint === 'across-lifeline'}
            tone="bad"
            onClick={() => update({ pressurePoint: 'across-lifeline' })}
          />
          <Choice
            label="In the meaty palm / heel of hand"
            sub="✗ Worst case — bow is fighting your wrist's natural rotation."
            selected={data.pressurePoint === 'meaty-palm'}
            tone="bad"
            onClick={() => update({ pressurePoint: 'meaty-palm' })}
          />
          <Choice
            label="Not sure — never checked"
            selected={data.pressurePoint === 'unsure'}
            tone="neutral"
            onClick={() => update({ pressurePoint: 'unsure' })}
          />
        </div>
      </Card>

      {/* ───────────── Open-finger / flutter test ───────────── */}
      <Card padding="lg">
        <h3 className="font-semibold mb-2">Loose-finger test</h3>
        <p className="text-[12px] text-zinc-400 mb-3 leading-relaxed">
          At full draw, your front fingers should be loose enough to wiggle. If you
          can't flutter them — you're gripping. The principle:{' '}
          <em>those front fingers can hang completely loose; they're not applying any
          pressure to the riser.</em>
        </p>
        <div className="grid grid-cols-2 gap-2">
          <Choice
            label="Yes — fingers can flutter at full draw"
            selected={data.fingersRelaxed === true}
            tone="good"
            onClick={() => update({ fingersRelaxed: true })}
          />
          <Choice
            label="No — fingers are stiff / wrapped / squeezing"
            selected={data.fingersRelaxed === false}
            tone="bad"
            onClick={() => update({ fingersRelaxed: false })}
          />
        </div>
      </Card>

      {/* ───────────── Bubble-fight diagnostic ───────────── */}
      <Card padding="lg">
        <h3 className="font-semibold mb-2">Bubble fight — are you constantly leveling?</h3>
        <p className="text-[12px] text-zinc-400 mb-3 leading-relaxed">
          If your sight bubble keeps drifting at full draw and you're correcting it
          shot after shot, your hand is torquing the bow. <em>If you're fighting your
          bubble, your bow is talking to you.</em> A neutral grip leaves the bubble
          where you put it.
        </p>
        <div className="grid grid-cols-2 gap-2">
          <Choice
            label="No — bubble stays where I put it"
            selected={data.bubbleFight === false}
            tone="good"
            onClick={() => update({ bubbleFight: false })}
          />
          <Choice
            label="Yes — I'm always correcting the level"
            selected={data.bubbleFight === true}
            tone="bad"
            onClick={() => update({ bubbleFight: true })}
          />
        </div>
      </Card>

      {/* ───────────── Wrist sling ───────────── */}
      <Card padding="md">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={!!data.wristSling}
            onChange={(e) => update({ wristSling: e.target.checked })}
            className="mt-1 w-4 h-4 accent-ember-500"
          />
          <div className="flex-1">
            <p className="text-sm font-medium">I'm using a wrist sling</p>
            <p className="text-[12px] text-zinc-500 mt-0.5">
              A wrist sling lets you keep your fingers loose without dropping the
              bow at the shot. Without one, most archers grip — defeats the whole
              loose-finger principle. Required for a clean grip.
            </p>
          </div>
        </label>
      </Card>

      {/* ───────────── Contextual coaching ───────────── */}

      {isHighWrist && (
        <TipCard title="High wrist — what it's doing to your shot" defaultOpen>
          <p className="mb-2">
            <em>
              With a high wrist, your draw length will feel shorter than usual.
              You'll compensate by leaning forward, bending your bow arm, or
              lifting your shoulder — all of which compromise accuracy.
            </em>
          </p>
          <p className="mb-2">
            Re-set your hand: open the palm, find the lifeline, place the bow's
            grip on the thumb-side of the lifeline. Wrist relaxed BACK, not cocked
            up. Knuckles rotated outward ~45°.
          </p>
          <p className="text-[11px] text-zinc-400">
            If your bow's grip throat is too thick to allow a neutral hand position,
            a thinner aftermarket grip (Shrewd, Bowmar, or a flat side-plate setup)
            may be the cleanest fix.
          </p>
        </TipCard>
      )}

      {isLowWrist && (
        <TipCard title="Low wrist — works if the grip fits your hand" defaultOpen>
          <p className="mb-2">
            Low wrist (palm flat against the grip back) gets bone-to-bone contact
            and can be very stable. It only works if the grip's back surface fits
            flat to your palm without a pressure point.
          </p>
          <p className="mb-2">
            <strong>Test:</strong> if pressure feels concentrated anywhere other
            than dead-center on your palm, the grip isn't right for low wrist —
            switch to neutral or fit a different grip plate.
          </p>
        </TipCard>
      )}

      {pressureWrong && (
        <TipCard title="Lifeline fix — re-set the bow hand" defaultOpen>
          <p className="mb-2">
            Open your bow hand palm-up. Place your other index finger on the lifeline
            crease (the deep crease running from below the index, around the thumb pad).
            Set the bow's grip so its centerline lands on the <strong>thumb side</strong>{' '}
            of that crease — NOT across it.
          </p>
          <p className="mb-2">
            <strong>You should feel:</strong> pressure ONLY on the thumb pad. The
            heel of your hand should be <em>off</em> the grip entirely. Front
            fingers loose. Thumb pointing toward the target at about 45°.
          </p>
          <p className="text-[11px] text-zinc-400">
            After re-setting, draw and look at your bow. If it sits at a slight
            angle to your forearm — that's correct neutral grip. A bow that lines
            up perfectly with your forearm usually means you've buried it too deep.
          </p>
        </TipCard>
      )}

      {data.fingersRelaxed === false && (
        <TipCard title="Fingers are squeezing — the death grip" defaultOpen>
          <p className="mb-2">
            Gripping is the instinct that creates torque. The fix is psychological as
            much as physical: <strong>trust the wrist sling.</strong> Without it, your
            brain won't let the fingers loose because the bow will fall. With it, the
            bow can't escape — the fingers can finally let go.
          </p>
          <p className="mb-2">
            Drill: at full draw, consciously wiggle / flutter your front fingers
            before every shot. After 50-100 reps, loose fingers become the default.
          </p>
        </TipCard>
      )}

      {data.bubbleFight && (
        <TipCard title="Bubble fight — diagnose before re-tuning" defaultOpen>
          <p className="mb-2">
            Before you blame your sight or your level, check grip torque. A canted
            bow at full draw rotates the peep AND tilts the sight. If your level keeps
            drifting LEFT (RH shooter), your thumb is likely pressing the riser at 12
            o'clock or your palm is too deep — both rotate the bow clockwise from
            your view.
          </p>
          <p className="text-[11px] text-zinc-400">
            Drift RIGHT (RH) usually means a thumb wrapping around at 3 o'clock or
            heel-of-palm pressure pushing the riser counter-clockwise.
          </p>
        </TipCard>
      )}

      {!data.wristSling && (
        <WarningCard title="Add a wrist sling before continuing" defaultOpen>
          Without a wrist sling you'll keep gripping out of fear of dropping the bow,
          and no amount of coaching will fix that. Any $10 sling does the job — install
          one before working on grip.
        </WarningCard>
      )}

      {/* ───────────── Success state ───────────── */}
      {isNeutral &&
        data.pressurePoint === 'thumb-pad-lifeline' &&
        data.fingersRelaxed === true &&
        data.bubbleFight === false &&
        data.wristSling === true && (
          <div className="rounded-lg border border-emerald-700/40 bg-emerald-950/20 p-3 text-sm">
            <p className="font-semibold text-emerald-300 mb-1">Grip is dialed.</p>
            <p className="text-zinc-300">
              Move to cam timing. If a future tune step reveals persistent L/R drift
              that won't resolve, come back here — grip torque is the #1 cause of
              phantom tuning problems.
            </p>
          </div>
        )}

      <InfoCard title="The order matters">
        Grip → cam pre-lean → cam timing → cam sync → tiller → center shot → paper.
        Every step downstream assumes the step above is locked. A wrong grip is the
        most common reason a "fully tuned" bow shoots inconsistently at distance.
      </InfoCard>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────
// Grip diagram — 3 grip styles × 3 views (side / palm / front)
// SVG so it scales cleanly on mobile + tablet + desktop.
// ─────────────────────────────────────────────────────────────────

function GripDiagram() {
  return (
    <div className="overflow-x-auto">
      <svg
        viewBox="0 0 720 380"
        className="w-full max-w-[720px] mx-auto"
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="Bow grip diagram showing neutral, high-wrist, and low-wrist grips from three angles"
      >
        {/* Background */}
        <rect width="720" height="380" fill="#0a0a0a" rx="8" />

        {/* Column headers */}
        <text x="120" y="22" fill="#a1a1aa" fontFamily="monospace" fontSize="11" textAnchor="middle">
          SIDE VIEW (wrist angle)
        </text>
        <text x="360" y="22" fill="#a1a1aa" fontFamily="monospace" fontSize="11" textAnchor="middle">
          PALM VIEW (pressure point)
        </text>
        <text x="600" y="22" fill="#a1a1aa" fontFamily="monospace" fontSize="11" textAnchor="middle">
          FRONT VIEW (looking at target)
        </text>

        {/* Row 1: NEUTRAL (good) */}
        <text x="10" y="80" fill="#22c55e" fontFamily="monospace" fontSize="11" fontWeight="bold">
          NEUTRAL ✓
        </text>
        {/* Side view — neutral wrist, slight back tilt */}
        <g transform="translate(40, 50)">
          <rect x="60" y="20" width="14" height="80" fill="#3f3f46" stroke="#52525b" />
          <text x="44" y="60" fill="#71717a" fontSize="9" fontFamily="monospace">grip</text>
          {/* forearm angled in */}
          <line x1="74" y1="55" x2="160" y2="80" stroke="#22c55e" strokeWidth="14" strokeLinecap="round" opacity="0.6" />
          {/* hand glyph */}
          <ellipse cx="80" cy="55" rx="12" ry="14" fill="#22c55e" opacity="0.7" />
          <text x="100" y="35" fill="#22c55e" fontSize="9">~15° back</text>
        </g>
        {/* Palm view — lifeline + correct pressure */}
        <g transform="translate(280, 50)">
          {/* palm outline */}
          <ellipse cx="80" cy="50" rx="55" ry="40" fill="#27272a" stroke="#52525b" />
          {/* thumb */}
          <ellipse cx="30" cy="40" rx="14" ry="20" fill="#27272a" stroke="#52525b" transform="rotate(-25 30 40)" />
          {/* lifeline */}
          <path d="M 35 35 Q 50 28 75 30 Q 95 35 105 60" stroke="#a1a1aa" strokeWidth="1.5" strokeDasharray="3 2" fill="none" />
          <text x="115" y="62" fill="#a1a1aa" fontSize="8">lifeline</text>
          {/* correct pressure dot */}
          <circle cx="48" cy="42" r="6" fill="#22c55e" />
          <text x="40" y="92" fill="#22c55e" fontSize="9">thumb pad ✓</text>
        </g>
        {/* Front view — thumb at 45° */}
        <g transform="translate(530, 50)">
          {/* riser cross-section */}
          <rect x="55" y="30" width="20" height="50" fill="#3f3f46" stroke="#52525b" />
          {/* hand wrapping */}
          <ellipse cx="65" cy="55" rx="35" ry="25" fill="none" stroke="#22c55e" strokeWidth="2" opacity="0.7" />
          {/* thumb at 45° */}
          <line x1="65" y1="55" x2="95" y2="25" stroke="#22c55e" strokeWidth="5" strokeLinecap="round" />
          <circle cx="95" cy="25" r="4" fill="#22c55e" />
          <text x="98" y="22" fill="#22c55e" fontSize="9">thumb 45°</text>
          {/* loose fingers indicator */}
          <text x="20" y="92" fill="#22c55e" fontSize="9">fingers loose ✓</text>
        </g>

        {/* Divider */}
        <line x1="10" y1="125" x2="710" y2="125" stroke="#27272a" strokeWidth="1" />

        {/* Row 2: HIGH WRIST (bad) */}
        <text x="10" y="170" fill="#ef4444" fontFamily="monospace" fontSize="11" fontWeight="bold">
          HIGH ✗
        </text>
        {/* Side view — wrist cocked up */}
        <g transform="translate(40, 140)">
          <rect x="60" y="20" width="14" height="80" fill="#3f3f46" stroke="#52525b" />
          {/* forearm going down — wrist cocked up creates that angle */}
          <line x1="74" y1="55" x2="160" y2="100" stroke="#ef4444" strokeWidth="14" strokeLinecap="round" opacity="0.6" />
          <ellipse cx="80" cy="40" rx="12" ry="14" fill="#ef4444" opacity="0.7" />
          <text x="100" y="30" fill="#ef4444" fontSize="9">wrist cocked UP</text>
        </g>
        {/* Palm view — wrong, pressure across lifeline */}
        <g transform="translate(280, 140)">
          <ellipse cx="80" cy="50" rx="55" ry="40" fill="#27272a" stroke="#52525b" />
          <ellipse cx="30" cy="40" rx="14" ry="20" fill="#27272a" stroke="#52525b" transform="rotate(-25 30 40)" />
          <path d="M 35 35 Q 50 28 75 30 Q 95 35 105 60" stroke="#a1a1aa" strokeWidth="1.5" strokeDasharray="3 2" fill="none" />
          {/* wrong pressure — meaty palm pad on thumb side / throat */}
          <circle cx="55" cy="32" r="6" fill="#ef4444" />
          <text x="35" y="92" fill="#ef4444" fontSize="9">meaty thumb pad ✗</text>
        </g>
        {/* Front view — thumb at 12 o'clock, torque arrow */}
        <g transform="translate(530, 140)">
          <rect x="55" y="30" width="20" height="50" fill="#3f3f46" stroke="#52525b" />
          <ellipse cx="65" cy="55" rx="35" ry="25" fill="none" stroke="#ef4444" strokeWidth="2" opacity="0.7" />
          {/* thumb pointing up (12 o'clock) */}
          <line x1="65" y1="55" x2="65" y2="15" stroke="#ef4444" strokeWidth="5" strokeLinecap="round" />
          <circle cx="65" cy="15" r="4" fill="#ef4444" />
          <text x="40" y="10" fill="#ef4444" fontSize="9">thumb 12 o'clock</text>
          {/* torque arrow — clockwise = left impact RH */}
          <path d="M 30 80 A 20 20 0 0 1 60 90" stroke="#ef4444" strokeWidth="2" fill="none" markerEnd="url(#arrR)" />
          <text x="20" y="105" fill="#ef4444" fontSize="9">torque → LEFT miss (RH)</text>
        </g>

        {/* Divider */}
        <line x1="10" y1="215" x2="710" y2="215" stroke="#27272a" strokeWidth="1" />

        {/* Row 3: LOW WRIST (warning) */}
        <text x="10" y="260" fill="#f59e0b" fontFamily="monospace" fontSize="11" fontWeight="bold">
          LOW ⚠
        </text>
        {/* Side view — wrist flat */}
        <g transform="translate(40, 230)">
          <rect x="60" y="20" width="14" height="80" fill="#3f3f46" stroke="#52525b" />
          {/* forearm flat */}
          <line x1="74" y1="55" x2="160" y2="55" stroke="#f59e0b" strokeWidth="14" strokeLinecap="round" opacity="0.6" />
          <ellipse cx="80" cy="55" rx="12" ry="14" fill="#f59e0b" opacity="0.7" />
          <text x="100" y="40" fill="#f59e0b" fontSize="9">wrist flat</text>
        </g>
        {/* Palm view — pressure on whole palm (back of grip) */}
        <g transform="translate(280, 230)">
          <ellipse cx="80" cy="50" rx="55" ry="40" fill="#27272a" stroke="#52525b" />
          <ellipse cx="30" cy="40" rx="14" ry="20" fill="#27272a" stroke="#52525b" transform="rotate(-25 30 40)" />
          <path d="M 35 35 Q 50 28 75 30 Q 95 35 105 60" stroke="#a1a1aa" strokeWidth="1.5" strokeDasharray="3 2" fill="none" />
          {/* pressure across the back of palm */}
          <rect x="60" y="35" width="55" height="25" fill="#f59e0b" opacity="0.4" rx="3" />
          <text x="55" y="92" fill="#f59e0b" fontSize="9">flat palm contact</text>
        </g>
        {/* Front view — thumb still 45° but flat hand */}
        <g transform="translate(530, 230)">
          <rect x="55" y="30" width="20" height="50" fill="#3f3f46" stroke="#52525b" />
          <ellipse cx="65" cy="55" rx="35" ry="25" fill="none" stroke="#f59e0b" strokeWidth="2" opacity="0.7" />
          <line x1="65" y1="55" x2="95" y2="35" stroke="#f59e0b" strokeWidth="5" strokeLinecap="round" />
          <text x="20" y="95" fill="#f59e0b" fontSize="9">OK if grip fits flat</text>
        </g>

        {/* Bottom legend */}
        <line x1="10" y1="305" x2="710" y2="305" stroke="#27272a" strokeWidth="1" />
        <text x="20" y="328" fill="#a1a1aa" fontSize="10" fontFamily="monospace">
          KEY:
        </text>
        <circle cx="80" cy="324" r="5" fill="#22c55e" />
        <text x="92" y="328" fill="#d4d4d8" fontSize="10">correct</text>
        <circle cx="160" cy="324" r="5" fill="#ef4444" />
        <text x="172" y="328" fill="#d4d4d8" fontSize="10">wrong — induces torque</text>
        <circle cx="335" cy="324" r="5" fill="#f59e0b" />
        <text x="347" y="328" fill="#d4d4d8" fontSize="10">conditional — works only if grip fits</text>

        <text x="20" y="356" fill="#71717a" fontSize="9" fontFamily="monospace">
          Method: Lifeline grip (thumb-side pressure on the natural palm crease).
        </text>
        <text x="20" y="370" fill="#71717a" fontSize="9" fontFamily="monospace">
          Cross-ref: Elite Archery (Woods, Sidebottom), Lancaster Archery Supply, Bow International.
        </text>

        {/* Arrow marker for torque indicator */}
        <defs>
          <marker id="arrR" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#ef4444" />
          </marker>
        </defs>
      </svg>
    </div>
  );
}

function Choice({
  label,
  sub,
  selected,
  onClick,
  tone = 'neutral',
}: {
  label: string;
  sub?: string;
  selected: boolean;
  onClick: () => void;
  tone?: 'good' | 'bad' | 'warn' | 'neutral';
}) {
  const selectedTone =
    tone === 'good'
      ? 'border-emerald-500 bg-emerald-500/10'
      : tone === 'bad'
        ? 'border-rose-500 bg-rose-500/10'
        : tone === 'warn'
          ? 'border-amber-500 bg-amber-500/10'
          : 'border-ember-500 bg-ember-500/10';
  return (
    <button
      onClick={onClick}
      className={
        'w-full text-left rounded-md border px-3 py-2.5 text-sm transition ' +
        (selected
          ? `${selectedTone} text-zinc-100`
          : 'border-zinc-800 bg-zinc-950 text-zinc-300 hover:border-zinc-700')
      }
    >
      <p className="font-medium leading-snug">{label}</p>
      {sub && <p className="text-[11px] text-zinc-500 mt-0.5 leading-snug">{sub}</p>}
    </button>
  );
}
